import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState<boolean | null>(null);

  useEffect(() => {
    // Recovery links arrive with #type=recovery; Supabase fires PASSWORD_RECOVERY.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setValid(true);
    });
    if (window.location.hash.includes("type=recovery")) setValid(true);
    const t = setTimeout(() => setValid((v) => (v === null ? false : v)), 3000);
    return () => { sub.subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Password updated. You are now signed in.");
    navigate("/admin/blog", { replace: true });
  };

  return (
    <Layout>
      <Seo title="Reset Password" description="Set a new admin password." />
      <section className="container-page py-16 max-w-md">
        <h1 className="font-display text-3xl font-bold mb-2">Reset Password</h1>
        {valid === null && <p className="text-muted-foreground text-sm">Checking your reset link…</p>}
        {valid === false && (
          <p className="text-muted-foreground text-sm">
            This reset link is invalid or has expired. Go back to the{" "}
            <a href="/admin/login" className="text-accentInk hover:underline">sign in page</a> and request a new one.
          </p>
        )}
        {valid && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-muted-foreground mb-2 text-sm">Choose a new password for your admin account.</p>
            <div>
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Please wait…" : "Update password"}
            </Button>
          </form>
        )}
      </section>
    </Layout>
  );
}
